import React, { useState } from 'react';
import { KeyRound, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { commsApi } from '@/utils/commsApi';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

const MIN_LENGTH = 8;

const Field = ({ id, label, value, onChange, placeholder, testId, autoFocus }) => (
  <div className="space-y-2">
    <Label htmlFor={id}>{label}</Label>
    <Input id={id} type="password" autoComplete={id === 'current-password' ? 'current-password' : 'new-password'} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} autoFocus={autoFocus} data-testid={testId} />
  </div>
);

// Accounts that already have a password: rotate it (other devices get signed out).
export const ChangePasswordDialog = ({ open, onOpenChange, user, onSaved }) => {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);

  const validation = (() => {
    if (!next) return null;
    if (next.length < MIN_LENGTH) return `Use at least ${MIN_LENGTH} characters`;
    if (next.trim().toLowerCase() === (user.email || '').trim().toLowerCase()) return 'Your password cannot be your email address';
    if (current && next === current) return 'Pick a password different from the current one';
    if (confirm && confirm !== next) return 'Passwords do not match';
    return null;
  })();
  const ready = current.length > 0 && next.length >= MIN_LENGTH && confirm === next && !validation;

  const close = (isOpen) => {
    if (!isOpen) { setCurrent(''); setNext(''); setConfirm(''); }
    onOpenChange(isOpen);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!ready || busy) return;
    setBusy(true);
    try {
      const { user: updated } = await commsApi.changePassword(current, next, confirm);
      onSaved?.(updated);
      toast.success('Password changed. Other devices have been signed out.');
      close(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not change the password');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="sm:max-w-md" data-testid="change-password-dialog">
        <form onSubmit={submit} className="space-y-4">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><KeyRound size={18} /> Change password</DialogTitle>
            <DialogDescription>Choose a new password for <strong>{user.email}</strong>. You stay signed in here; other devices will need to sign in again.</DialogDescription>
          </DialogHeader>
          <Field id="current-password" label="Current password" value={current} onChange={setCurrent} placeholder="Your current password" testId="change-password-current" autoFocus />
          <Field id="new-password" label="New password" value={next} onChange={setNext} placeholder={`At least ${MIN_LENGTH} characters`} testId="change-password-new" />
          <Field id="confirm-password" label="Confirm new password" value={confirm} onChange={setConfirm} placeholder="Type it again" testId="change-password-confirm" />
          <p className={`text-sm min-h-5 ${validation ? 'text-red-600' : 'text-slate-500'}`} data-testid="change-password-hint">
            {validation || `Minimum ${MIN_LENGTH} characters and not your email address.`}
          </p>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => close(false)} disabled={busy} data-testid="change-password-cancel">Cancel</Button>
            <Button type="submit" disabled={!ready || busy} data-testid="change-password-submit">
              {busy ? <Loader2 size={16} className="animate-spin" /> : 'Change password'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
